import { ButtonWhite } from "@@/shared/buttons_v2/button-gray";
import {
    HorizontalDivider,
    HorizontalLine,
    VerticalDivider,
} from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { ForEach } from "@@/shared/for-each";
import { Picture } from "@@/shared/pictures/picture";
import { TextBox } from "@@/shared/text";
import { useTheme } from "@emotion/react";
import {
    groupBy,
    translation,
    type MetaImage,
    type StorageItemImage,
    type Ticket,
    type Translatable,
} from "@towni/common";
import { useNavigate } from "react-router";

type Props = {
    tickets: Ticket[];
};

const borderRadius = 14;

// This component is used in the DigitalVoucherReceipt_Tickets_V2 component
// When showing multiple tickets in a receipt
const TicketReceiptViewV2 = (props: Props) => {
    const link = `/tickets/${props.tickets[0].orderGroupId}`;
    const showText =
        props.tickets.length > 1
            ? translation({
                  sv: "Visa värdecheckar",
                  en: "Show vouchers",
              })
            : translation({
                  sv: "Visa värdecheck",
                  en: "Show voucher",
              });

    const theme = useTheme();
    const navigate = useNavigate();

    const ticketGroupByProductId = groupBy(
        props.tickets,
        ticket => ticket.productId,
    );

    const productIds = Array.from(ticketGroupByProductId.keys());

    return (
        <FlexColumn
            maxWidth={310}
            minWidth={310}
            css={{
                position: "relative",
                willChange: "filter",
                borderTopLeftRadius: borderRadius,
                borderTopRightRadius: borderRadius,
                borderRadius: borderRadius,
                overflow: "hidden",
                backgroundColor: theme.colors.transparent.asString,
                filter: `drop-shadow(0px 3.80066px 9.50166px ${
                    theme.colors.black.value.withAlpha(0.15).asString
                })`,
            }}>
            <FlexRow
                mainAxis="center"
                fillParentWidth
                css={{ position: "absolute", zIndex: 15, marginTop: 48 }}>
                <ButtonWhite
                    onClick={() => {
                        if (link) void navigate(link);
                    }}
                    css={{
                        borderRadius: 3000,
                        willChange: "filter",
                        filter: `drop-shadow(0px 2px 1px ${
                            theme.colors.black.value.withAlpha(0.2).asString
                        })`,
                    }}>
                    <TextBox
                        text={showText}
                        color={theme.colors.primary}
                        weight="600"
                    />
                </ButtonWhite>
            </FlexRow>
            <TicketHeader
                items={productIds.flatMap(productId => {
                    const tickets = ticketGroupByProductId.get(productId);

                    if (!tickets) {
                        return [];
                    }

                    return {
                        quantity: tickets.length,
                        title: tickets[0].meta.title,
                        subtitle: tickets[0].meta.subtitle,
                        backgroundImage: tickets[0].meta.images[0],
                    };
                })}
                link={link}
            />
        </FlexColumn>
    );
};

const TicketHeader = (props: {
    items: {
        quantity: number;
        title: Translatable;
        subtitle: Translatable;
        backgroundImage: MetaImage | StorageItemImage;
    }[];
    link?: string;
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { items, link } = props;

    const backgroundImages = items
        .map(item => item.backgroundImage)
        .filter(Boolean);

    const backgroundImage =
        backgroundImages.length > 0 ? backgroundImages[0] : undefined;

    return (
        <FlexColumn
            fillParentWidth
            onClick={() => {
                if (link) void navigate(link);
            }}
            css={{ position: "relative", minHeight: 210 }}
            background={{ color: theme.colors.white }}>
            {backgroundImage ? (
                <Picture
                    imageRef={backgroundImage}
                    minHeight={130}
                    height={130}
                    imageOptions={{ imageHeight: 130, imageWidth: 310 }}
                />
            ) : (
                <FlexRow css={{ height: 130 }} />
            )}

            {/* <Conditional when={!!logo}>
                <FlexRow
                    fillParentWidth
                    padding={{ all: 10 }}
                    css={{ position: "relative", zIndex: 10, marginTop: -60 }}>
                    <LogoCircle
                        logo={logo}
                        size={0.5 as RemSize}
                        boxShadow={`0px 4px 10px ${
                            theme.colors.black.value.withAlpha(0.1).asString
                        }`}
                    />
                </FlexRow>
            </Conditional> */}
            <ForEach itemOf={items} divider={<HorizontalLine />}>
                {item => (
                    <>
                        <FlexRow padding={{ leftRight: 20, top: 15 }}>
                            <TextBox
                                text={item.quantity.toString()}
                                size={1.1875}
                                weight="900"
                                color={theme.colors.black}
                            />
                            <HorizontalDivider XXS />
                            <TextBox
                                text={"x"}
                                size={1.1875}
                                weight="700"
                                color={theme.colors.black}
                            />
                            <HorizontalDivider XXS />
                            <TextBox
                                text={item.title}
                                size={1.1875}
                                weight="900"
                                color={theme.colors.black}
                            />
                        </FlexRow>

                        <VerticalDivider XXS />
                        <TextBox
                            text={item.subtitle}
                            padding={{ leftRight: 20, bottom: 5 }}
                            size={0.95}
                            color={theme.colors.black.light30}
                        />
                    </>
                )}
            </ForEach>
            <VerticalDivider M />
        </FlexColumn>
    );
};

export { TicketReceiptViewV2 };
